import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { RealtimeProvider } from "./context/RealtimeContext";
import { BrandProvider } from "./context/BrandContext";
import Shell from "./components/Shell";
import { Toaster } from "./components/ui/sonner";
import { OpeningSplash } from "./components/OpeningSplash";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Planning from "./pages/Planning";
import Agents from "./pages/Agents";
import Sites from "./pages/Sites";
import Clients from "./pages/Clients";
import Documents from "./pages/Documents";
import Messages from "./pages/Messages";
import Users from "./pages/Users";
import Prestations from "./pages/Prestations";
import Devis from "./pages/Devis";
import Factures from "./pages/Factures";
import Settings from "./pages/Settings";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Hours from "./pages/Hours";
import Reports from "./pages/Reports";
import PtiRondes from "./pages/PtiRondes";
import MainCourante from "./pages/MainCourante";
import PcsPosts from "./pages/PcsPosts";
import Pcs from "./pages/Pcs";
import ReportTemplates from "./pages/ReportTemplates";
import ConsignesCles from "./pages/ConsignesCles";
import PublicVisitorAccess from "./pages/PublicVisitorAccess";
import AgentApp from "./pages/AgentApp";
import SignDevis from "./pages/SignDevis";
import Xaltix from "./pages/Xaltix";
import Privacy from "./pages/Privacy";
import LegalNotice from "./pages/LegalNotice";
import Prepaie from "./pages/Prepaie";
import SiteHome from "./pages/site/SiteHome";
import { SitePole, SiteContact, SiteLegal } from "./pages/site/SitePages";
import SiteContacts from "./pages/SiteContacts";
import Alerts from "./pages/Alerts";
import XaltixLeads from "./pages/XaltixLeads";
import SignAgentDoc from "./pages/SignAgentDoc";
import ApplyCandidate from "./pages/ApplyCandidate";
import Candidates from "./pages/Candidates";
import DocTemplates from "./pages/DocTemplates";
import AgentDocs from "./pages/AgentDocs";
import Pointages from "./pages/Pointages";
import AuditLog from "./pages/AuditLog";
import CoverageMap from "./pages/CoverageMap";
import PublicCoverage from "./pages/PublicCoverage";
import EmbedCoverage from "./pages/EmbedCoverage";
import QuoteRequest from "./pages/QuoteRequest";
import QuoteRequests from "./pages/QuoteRequests";
import Compliance from "./pages/Compliance";
import SuperCompanies from "./pages/SuperCompanies";
import Support from "./pages/Support";
import SuperBilling from "./pages/SuperBilling";
import SuperSettings from "./pages/SuperSettings";
import SignXaltixQuote from "./pages/SignXaltixQuote";
import PayXaltixInvoice from "./pages/PayXaltixInvoice";
import SuperDashboard from "./pages/SuperDashboard";
import SuperTeam from "./pages/SuperTeam";
import SuperAudience from "./pages/SuperAudience";
import Materiel from "./pages/Materiel";
import SuperMap from "./pages/SuperMap";
import SuperCandidates from "./pages/SuperCandidates";
import Subcontractors from "./pages/Subcontractors";
import { hasPerm, isTeam, firstTeamPath } from "./lib/team";
import { canAccess, firstAllowedPath } from "./lib/sections";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen grid place-items-center text-slate-500 text-sm">Chargement…</div>;
  // Non connecté → on renvoie vers la landing publique "Postuler" (mise en avant),
  // pas vers /login. L'accès admin reste discret via le bouton en haut à droite.
  if (!user) return <Navigate to="/accueil" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function SuperOnly({ children, perm }) {
  const { user } = useAuth();
  if (user?.is_superadmin || (perm && hasPerm(user, perm))) return children;
  return <Navigate to={isTeam(user) ? firstTeamPath(user) : "/"} replace />;
}

function Guard({ section, module, children }) {
  const { user } = useAuth();
  if (isTeam(user)) return <Navigate to={firstTeamPath(user)} replace />;
  if (canAccess(user, section, module)) return children;
  const first = firstAllowedPath(user);
  if (first && first !== window.location.pathname) return <Navigate to={first} replace />;
  return (
    <div className="p-16 text-center" data-testid="no-access">
      <h1 className="font-display text-2xl font-extrabold mb-2">Accès non autorisé</h1>
      <p className="text-sm text-slate-600">Votre compte n'a accès à aucune rubrique pour le moment. Contactez l'administrateur.</p>
    </div>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <BrandProvider>
          <AuthProvider>
            <RealtimeProvider>
              <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/e/:slug" element={<Login />} />
              <Route path="/accueil" element={<SiteHome />} />
              <Route path="/pole/:slug" element={<SitePole />} />
              <Route path="/contact" element={<SiteContact />} />
              <Route path="/mentions-legales-solutions-prime" element={<SiteLegal />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/agent" element={<AgentApp />} />
              <Route path="/agent-app" element={<AgentApp />} />
              <Route path="/pcs/:postId" element={<Pcs />} />
              <Route path="/sign/devis/:token" element={<SignDevis />} />
              <Route path="/devis-xaltix/:token" element={<SignXaltixQuote />} />
              <Route path="/payer-xaltix/:token" element={<PayXaltixInvoice />} />
              <Route path="/acces-visiteurs/:token" element={<PublicVisitorAccess />} />
              <Route path="/sign/contrat/:token" element={<SignAgentDoc />} />
              <Route path="/postuler" element={<ApplyCandidate />} />
              <Route path="/postuler/:link" element={<ApplyCandidate />} />
              <Route path="/demande-devis/:link" element={<QuoteRequest />} />
              <Route path="/carte-publique" element={<PublicCoverage />} />
              <Route path="/carte-integree" element={<EmbedCoverage />} />
              <Route path="/demande-devis" element={<QuoteRequest />} />
              <Route path="/xaltix" element={<Xaltix />} />
              <Route path="/confidentialite" element={<Privacy />} />
              <Route path="/mentions-legales" element={<LegalNotice />} />
              <Route path="/" element={<Protected><Shell /></Protected>}>
                <Route index element={<Guard section="dashboard"><Dashboard /></Guard>} />
                <Route path="planning" element={<Guard section="planning"><Planning /></Guard>} />
                <Route path="contacts-xaltix" element={<SuperOnly perm="leads"><XaltixLeads /></SuperOnly>} />
                <Route path="super/tableau-de-bord" element={<SuperOnly perm="revenue"><SuperDashboard /></SuperOnly>} />
                <Route path="super/entreprises" element={<SuperOnly perm="companies_read"><SuperCompanies /></SuperOnly>} />
                <Route path="super/service-client" element={<SuperOnly perm="support"><Support /></SuperOnly>} />
                <Route path="super/facturation" element={<SuperOnly perm={["quotes", "invoices", "revenue"]}><SuperBilling /></SuperOnly>} />
                <Route path="super/parametres" element={<SuperOnly><SuperSettings /></SuperOnly>} />
                <Route path="super/equipe" element={<SuperOnly><SuperTeam /></SuperOnly>} />
                <Route path="super/audience" element={<SuperOnly><SuperAudience /></SuperOnly>} />
                <Route path="super/carte" element={<SuperOnly perm={["companies_read", "revenue"]}><SuperMap /></SuperOnly>} />
                <Route path="super/candidatures" element={<SuperOnly><SuperCandidates /></SuperOnly>} />
                <Route path="super/aucun-acces" element={<div className="p-10 text-sm text-slate-600" data-testid="no-access">Votre profil ne donne accès à aucune rubrique. Contactez le super-administrateur.</div>} />
                <Route path="service-client" element={<Support />} />
                <Route path="alertes" element={<Guard><Alerts /></Guard>} />
                <Route path="conformite" element={<Guard section="planning"><Compliance /></Guard>} />
                <Route path="agents" element={<Guard section="agents"><Agents /></Guard>} />
                <Route path="candidats" element={<Guard section="candidates"><Candidates /></Guard>} />
                <Route path="modeles-documents" element={<Guard section="doc_templates"><DocTemplates /></Guard>} />
                <Route path="documents-agents" element={<Guard section="agent_docs"><AgentDocs /></Guard>} />
                <Route path="heures" element={<Guard section="hours"><Hours /></Guard>} />
                <Route path="prepaie" element={<Guard section="prepaie"><Prepaie /></Guard>} />
                <Route path="contacts-site" element={<SuperOnly><SiteContacts /></SuperOnly>} />
                <Route path="pointages" element={<Guard section="pointages"><Pointages /></Guard>} />
                <Route path="rapports" element={<Guard section="reports"><Reports /></Guard>} />
                <Route path="pti-rondes" element={<Guard section="sites"><PtiRondes /></Guard>} />
                <Route path="main-courante" element={<Guard section="sites"><MainCourante /></Guard>} />
                <Route path="postes-securite" element={<Guard section="sites"><PcsPosts /></Guard>} />
                <Route path="modeles-rapports" element={<Guard section="sites"><ReportTemplates /></Guard>} />
                <Route path="consignes-cles" element={<Guard section="sites"><ConsignesCles /></Guard>} />
                <Route path="sites" element={<Guard section="sites"><Sites /></Guard>} />
                <Route path="materiel" element={<Guard section="materiel"><Materiel /></Guard>} />
                <Route path="sous-traitance" element={<Guard section="agents"><Subcontractors /></Guard>} />
                <Route path="carte" element={<Guard><CoverageMap /></Guard>} />
                <Route path="clients" element={<Guard section="clients"><Clients /></Guard>} />
                <Route path="prestations" element={<Guard section="prestations"><Prestations /></Guard>} />
                <Route path="devis" element={<Guard section="devis"><Devis /></Guard>} />
                <Route path="demandes-devis" element={<Guard section="devis" module="quote_requests"><QuoteRequests /></Guard>} />
                <Route path="factures" element={<Guard section="factures"><Factures /></Guard>} />
                <Route path="documents" element={<Guard section="documents"><Documents /></Guard>} />
                <Route path="messages" element={<Guard section="messages"><Messages /></Guard>} />
                <Route path="parametres" element={<Guard section="settings"><Settings /></Guard>} />
                <Route path="utilisateurs" element={<AdminOnly><Users /></AdminOnly>} />
                <Route path="journal" element={<AdminOnly><AuditLog /></AdminOnly>} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </RealtimeProvider>
          </AuthProvider>
        </BrandProvider>
      </BrowserRouter>
      <OpeningSplash />
      <Toaster richColors position="top-right" offset={64} />
    </div>
  );
}
